/**
 * Structural changes, customer edition.
 *
 * This was a deliberate stub for months — the portal offered no structural
 * UI, so the client sent nothing. Phase 4 (portal gallery editing) makes it
 * real: the call goes to the standard /api/staging/:websiteId/structural-change
 * route, which admits an OWNING customer session (route-auth) and then
 * restricts customer sessions to the four gallery photo actions behind the
 * per-customer gallery_editing flag (customerStructuralGate, server-side).
 * The UI hides what the server would refuse; the server does the refusing.
 */

import { apiPost } from './client';

export type StructuralAction =
  | 'add_gallery_image'
  | 'remove_gallery_image'
  | 'swap_gallery_images'
  | 'move_gallery_image'
  // Staff-only actions a customer session will be refused; present so the
  // shared editor components type-check identically in both apps.
  | 'add_section'
  | 'remove_section'
  | 'reorder_sections'
  | 'add_page'
  | 'add_page_with_content'
  | 'remove_page'
  | 'update_navigation'
  | 'reorder_navigation'
  | 'change_section_variant'
  | 'change_section_background'
  | 'add_service_item'
  | 'remove_service_item'
  | 'add_faq_item'
  | 'remove_faq_item'
  | 'add_testimonial'
  | 'remove_testimonial';

export interface StructuralTarget {
  page?: string;
  section?: string;
  elementPath?: string;
  index?: number;
}

export interface StructuralChange {
  id: string;
  action: StructuralAction;
  target: StructuralTarget;
  data: Record<string, unknown>;
  position?: number | 'start' | 'end' | 'after';
  afterSection?: string;
}

export interface StructuralChangeResult {
  success: boolean;
  content?: unknown;
  applied_changes?: StructuralChange[];
  error?: string;
}

export async function applyStructuralChanges(
  websiteId: string,
  changes: StructuralChange[]
): Promise<StructuralChangeResult> {
  return apiPost<StructuralChangeResult>(`/api/staging/${websiteId}/structural-change`, { changes });
}
